/**
 * Questions tab component for QualificationDetail.
 * Displays sections with questions, scoring, and response highlighting.
 */

import type { ReactNode } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { FileText, AlertTriangle } from 'lucide-react';
import { QualificationQuestion, QualificationTemplate } from '../../lib/api';
import { ResponseMap } from './types';
import { QuestionInput } from './QuestionInput';
import { SectionScoreHeader } from './SectionScoreHeader';
import { getScoreStatus } from './scoring-utils';

interface QuestionsTabProps {
  template: QualificationTemplate | null;
  responses: ResponseMap;
  qualificationStatus: string;
  isSupplier: boolean;
  passScore?: number;
  onResponseChange: (questionId: number, value: string | number | boolean | string[], questionType: string) => void;
  onFileUpload?: (questionId: number, file: File) => void;
  onFileDownload?: (questionId: number) => void;
  onFileRemove?: (questionId: number) => void;
  uploadingFiles?: Record<number, boolean>;
  /** Overrides the supplier-only edit rule (Audit Lite: the customer's auditor answers). */
  canEdit?: boolean;
  /** Extra controls under each question (Audit Lite: comment, finding, photo, actions). */
  renderQuestionFooter?: (question: QualificationQuestion) => ReactNode;
  /** Touch-sized inputs (Audit Lite on a tablet). */
  touch?: boolean;
}

export function QuestionsTab({
  template,
  responses,
  qualificationStatus,
  isSupplier,
  passScore = 70,
  onResponseChange,
  onFileUpload,
  onFileDownload,
  onFileRemove,
  uploadingFiles,
  canEdit,
  renderQuestionFooter,
  touch,
}: QuestionsTabProps) {
  if (!template?.sections || template.sections.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No questions available</h3>
          <p className="text-muted-foreground">
            The template does not have any sections or questions configured.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {template.sections
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((section) => (
          <Card key={section.id} id={`section-${section.id}`} className="scroll-mt-52">
            <SectionScoreHeader
              hideScores={isSupplier}
              section={section}
              responses={responses}
              passScore={passScore}
            />
            <CardContent className="space-y-6">
              {section.questions
                ?.sort((a, b) => a.sort_order - b.sort_order)
                .map((question, idx) => {
                  const response = responses[question.id];
                  const scoreStatus = question.is_scored
                    ? getScoreStatus(response?.score_awarded, question.max_score)
                    : null;

                  // Only show highlighting for scored questions with responses
                  const showHighlight = scoreStatus &&
                    scoreStatus.level !== 'neutral' &&
                    response?.score_awarded !== undefined;

                  return (
                    <div
                      key={question.id}
                      id={`question-${question.id}`}
                      className={`scroll-mt-52 space-y-2 p-3 rounded-lg border transition-colors ${
                        showHighlight
                          ? `${scoreStatus.borderClass} ${scoreStatus.bgClass}`
                          : 'border-transparent'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <Label id={`question-${question.id}-label`} className="text-base font-medium flex items-center gap-2">
                          {showHighlight && scoreStatus.level === 'failed' && (
                            <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
                          )}
                          {idx + 1}. {question.question_text}
                          {question.is_required && <span className="text-red-500 ml-1">*</span>}
                        </Label>
                        {/* Scoring is the buyer's view. A supplier that can see
                            which answer is worth what can simply pick the high
                            scorers, so weights, points and the running score are
                            hidden while they answer. */}
                        {question.is_scored && !isSupplier && (
                          <Badge
                            variant="outline"
                            className={`ml-2 flex-shrink-0 ${
                              showHighlight ? scoreStatus.textClass : ''
                            }`}
                          >
                            {response?.score_awarded ?? 0} / {question.max_score} pts
                          </Badge>
                        )}
                      </div>
                      {question.help_text && (
                        <p className="text-sm text-muted-foreground">{question.help_text}</p>
                      )}
                      <QuestionInput
                        question={question}
                        responses={responses}
                        qualificationStatus={qualificationStatus}
                        isSupplier={isSupplier}
                        onResponseChange={onResponseChange}
                        onFileUpload={onFileUpload}
                        onFileDownload={onFileDownload}
                        onFileRemove={onFileRemove}
                        isUploadingFile={uploadingFiles?.[question.id]}
                        canEdit={canEdit}
                        touch={touch}
                      />
                      {renderQuestionFooter?.(question)}
                    </div>
                  );
                })}
            </CardContent>
          </Card>
        ))}
    </div>
  );
}
